import type { Events } from './events.js';
import posthog from 'posthog-js';
import { validatePosthogHost } from '../posthog-config.js';
import { analyticsEnvironment, beforeSend, safePath, superProperties } from './privacy.js';
import { serializeEvent } from './payload.js';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

let gtmEnabled = false;
let initialized = false;
let initializationFailed = false;

export function posthogClient(): typeof posthog | undefined {
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!key || initializationFailed) return undefined;
  if (initialized) return posthog;
  try {
    const host = import.meta.env.VITE_POSTHOG_HOST?.trim();
    validatePosthogHost(key, host);
    posthog.init(key, {
      api_host: host,
      autocapture: false,
      capture_pageview: 'history_change',
      capture_pageleave: false,
      person_profiles: 'identified_only',
      respect_dnt: false,
      persistence: 'sessionStorage',
      save_referrer: false,
      save_campaign_params: false,
      disable_capture_url_hashes: true,
      capture_heatmaps: false,
      capture_dead_clicks: false,
      rageclick: false,
      capture_exceptions: false,
      capture_performance: false,
      disable_surveys: true,
      disable_product_tours: true,
      disable_conversations: true,
      disable_web_experiments: true,
      enable_recording_console_log: false,
      disable_session_recording: analyticsEnvironment !== 'prod',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        maskAllElementAttributes: true,
        blockSelector: 'input[type="hidden"], input[type="file"], canvas, svg, img, .ph-no-capture',
        captureCanvas: { recordCanvas: false },
        recordCrossOriginIframes: false,
        captureJsonLd: false,
        recordHeaders: false,
        recordBody: false,
        streamNetworkBody: false,
        maskCapturedNetworkRequestFn: (request) => ({
          ...request,
          name: `${window.location.origin}${safePath(request.name)}`,
        }),
      },
      before_send: beforeSend,
      loaded: (client) => client.register(superProperties),
    });
    initialized = true;
    return posthog;
  } catch {
    initializationFailed = true;
    console.warn('Analytics: PostHog could not be initialized.');
    return undefined;
  }
}

export function initializeAnalytics(): void {
  posthogClient();
  const id = import.meta.env.VITE_GTM_CONTAINER_ID?.trim();
  if (gtmEnabled || import.meta.env.VITE_ENVIRONMENT !== 'prod' || !id) return;
  if (!/^GTM-[A-Z0-9]+$/.test(id)) {
    console.warn('Analytics: invalid VITE_GTM_CONTAINER_ID; GTM is disabled.');
    return;
  }
  try {
    window.dataLayer ??= [];
    window.dataLayer.push({ event: 'gtm.js', 'gtm.start': Date.now() });
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
    script.onerror = () => console.warn('Analytics: GTM could not be loaded.');
    document.head.appendChild(script);
    gtmEnabled = true;
  } catch {
    console.warn('Analytics: GTM could not be initialized.');
  }
}

export function track<K extends keyof Events>(event: K, properties: Events[K]): void {
  const payload = serializeEvent(event, properties);
  if (!payload) {
    console.warn('Analytics: invalid event payload was dropped.');
    return;
  }
  try {
    posthogClient()?.capture(event, payload);
  } catch {
    console.warn('Analytics: PostHog event could not be delivered.');
  }
  if (!gtmEnabled) return;
  try {
    switch (event) {
      case 'cart_emailed':
        window.dataLayer?.push({ event, design_reference: payload.design_reference });
        break;
      case 'cart_summary_downloaded':
        window.dataLayer?.push({
          event,
          design_reference: payload.design_reference,
          line_count: payload.line_count,
          total: payload.total,
        });
        break;
      case 'checkout_clicked':
        window.dataLayer?.push({ event, channel: payload.channel });
    }
  } catch {
    console.warn('Analytics: GTM event could not be delivered.');
  }
}
